import Cookies from "js-cookie";
import { clearRedirectPath } from "@noom/hive-auth";
import type { useLogger } from "@noom/hive-analytics";

import { removeIdentityCookie } from "@modules/authentication/IdentityCookie";
import { IN_APP_LOGIN_KEY } from "@modules/common/constants";

import { clearPatientProfileCache } from "./PatientProfileProvider";

type MixpanelLogger = ReturnType<typeof useLogger>;

/**
 * Clears the client-side session state shared by logout and the non-patient
 * eject: the cross-app SSO identity cookie, the native auto-login cookie,
 * session-scoped storage, the cached patient profile, and the Mixpanel
 * identity / super-properties (e.g. `user_id`). Clearing the cookies and
 * Mixpanel identity prevents a prior session from bleeding into the next user
 * on a shared browser / WebView.
 *
 * Does NOT tear down the hive-auth session and does NOT redirect — callers do
 * that themselves (`<Logout>` in AppLogout, `clearAuthState()` + SecureRoute in
 * RequirePatient).
 *
 * NOTE: this clears `sessionStorage`, so a caller that relays a message to the
 * login screen (e.g. the enrollment error) must set it AFTER calling this.
 */
export const clearLocalSessionState = (mixpanelLogger: MixpanelLogger): void => {
  removeIdentityCookie();
  Cookies.remove(IN_APP_LOGIN_KEY);
  // Drop any saved post-login redirect path. Every password login leaves the
  // hive-auth redirect cookie set to `/login` (PublicRoute persists it while
  // bouncing the freshly-authenticated user off the login page), so without
  // this the NEXT user to log in on this tab is navigated straight back to
  // /login and can loop there instead of landing in the app.
  clearRedirectPath();
  sessionStorage.clear();
  clearPatientProfileCache();
  mixpanelLogger.reset();
};
